import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  useQpmRequirementGetQuery,
  useQpmRequirementPutMutation,
} from "@/services/backend/qpm/requirements/api/requirement-api";
import { PutRequirementRequest } from "@/services/backend/qpm/requirements/types/requirement";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { RequirementDTO } from "@/services/backend/qpm/requirements/types/requirement-dto";
import type { PestSelectionDTO } from "@/services/backend/qpm/requirements/types/pest-selection";
import type { ReqAdditionalInformationDTO } from "@/services/backend/qpm/requirements/types/requirement-additional-information";
import type {
  RequirementMonitorSummaryDTO,
  RequirementMonitorSummaryItemDTO,
  TypeSummaryItemDTO,
} from "@/services/backend/qpm/requirements/types/requirement-monitor-summary";
import type { RequirementMonitor } from "@/services/backend/qpm/requirements/types/requirement-monitor";
import type {
  MonitorSnapDTO,
  PestSnapDTO,
} from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import {
  MonitorIoTClass,
  MonitorType,
} from "@/services/backend/qpm/types/enums";
import {
  accumulateDerivedIoTDependents,
  supportsIoTCheckpointService,
} from "@/services/backend/qpm/requirements/lib/derived-iot-dependents";

type ID = string;

// Data context exposes the latest RTK query state for requirement fetching.
export type RequirementDataContextType = {
  requirement?: RequirementDTO;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => unknown;
};

const RequirementDataContext = createContext<
  RequirementDataContextType | undefined
>(undefined);

export function RequirementDataProvider({
  requirementId,
  children,
}: {
  requirementId: string;
  children: ReactNode;
}) {
  const { data, isLoading, isError, error, refetch } =
    useQpmRequirementGetQuery({ id: requirementId });

  const value = useMemo<RequirementDataContextType>(
    () => ({
      requirement: data,
      isLoading,
      isError,
      error,
      refetch,
    }),
    [data, isLoading, isError, error, refetch],
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <RequirementDataContext.Provider value={value}>
      {children}
    </RequirementDataContext.Provider>
  );
}

export const useRequirementData = () => {
  const context = useContext(RequirementDataContext);
  if (!context)
    throw new Error(
      "useRequirementData must be used within RequirementDataProvider",
    );
  return context;
};

export type RequirementEditContextType = {
  putRequest: PutRequirementRequest;
  updateRequirement: (
    updatedData:
      | Partial<RequirementDTO>
      | ((prev: RequirementDTO) => Partial<RequirementDTO>),
  ) => void;
};

const RequirementEditContext = createContext<
  RequirementEditContextType | undefined
>(undefined);

export function RequirementEditProvider({
  requirement,
  children,
}: {
  requirement: RequirementDTO;
  children: ReactNode;
}) {
  const [put, { isLoading, error, isSuccess, reset }] =
    useQpmRequirementPutMutation();
  const [putRequest, setPutRequest] = useState<PutRequirementRequest>(() => ({
    id: requirement.id,
    processId: requirement.processArtefactId!,
    requirement: recomputeRequirementDerivedFields({ ...requirement }),
  }));

  const updateRequirement = useCallback(
    (
      updatedData:
        | Partial<RequirementDTO>
        | ((prev: RequirementDTO) => Partial<RequirementDTO>),
    ) => {
      setPutRequest((prev) => {
        const patch =
          typeof updatedData === "function"
            ? updatedData(prev.requirement)
            : updatedData;

        const patchedRequirement: RequirementDTO = {
          ...prev.requirement,
          ...patch,
        };

        // Offline invalidation: recompute derived fields deterministically.
        const invalidated =
          recomputeRequirementDerivedFields(patchedRequirement);

        return {
          ...prev,
          requirement: invalidated,
        };
      });
    },
    [],
  );

  const value = useMemo<RequirementEditContextType>(
    () => ({
      putRequest,
      updateRequirement,
    }),
    [putRequest, updateRequirement],
  );

  useDebouncedMutationWithPersistenceStateContextUpdate(
    putRequest,
    put,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  return (
    <RequirementEditContext.Provider value={value}>
      {children}
    </RequirementEditContext.Provider>
  );
}

export const useRequirementEdit = (): RequirementEditContextType => {
  const context = useContext(RequirementEditContext);
  if (!context)
    throw new Error(
      "useRequirementEdit must be used within RequirementEditProvider",
    );
  return context;
};

/** ----------------------- Offline invalidation  -----------------------
 * Recomputes derived fields similar to backend ApplyMonitorChanges + pest reconciliation.
 *
 * Notes on performance:
 * - Single pass over selectedMonitors (O(n)).
 * - Constant-time lookups using maps.
 */

type PestSource = "DERIVED" | "MANUAL";

function shouldIncludeInCheckpointService(
  sel: RequirementMonitor,
  snap: MonitorSnapDTO | undefined,
): boolean {
  if (sel.monitorTypeKey === MonitorType.Checkpoint) {
    return sel.includeInCheckpointService ?? true;
  }
  if (sel.monitorTypeKey === MonitorType.FlyExterminator) {
    return sel.includeInCheckpointService ?? true;
  }
  if (sel.monitorTypeKey !== MonitorType.IoT) {
    return false;
  }
  const iotClass = sel.ioTSpec?.class ?? snap?.capabilities?.iot?.class;
  const fallback =
    snap?.capabilities?.includeInCheckpointService === true &&
    supportsIoTCheckpointService(iotClass);
  return (sel.includeInCheckpointService ?? fallback) === true;
}

function applyAdditionalInformationDefaults(
  additionalInformation: ReqAdditionalInformationDTO,
  selectedMonitors: RequirementMonitor[] | null | undefined,
  monitorSnaps: RequirementDTO["monitorSnaps"] | null | undefined,
): ReqAdditionalInformationDTO {
  const sels = selectedMonitors ?? [];

  // Derive flags in a single pass.
  const flags = sels.reduce(
    (acc, sel) => {
      if (sel.hasLethalAgent) acc.requiresSCB = true;
      if (sel.monitorTypeKey === MonitorType.IoT) acc.requiresALoRa = true;
      const snap = monitorSnaps?.[sel.monitorId];
      if (shouldIncludeInCheckpointService(sel, snap)) {
        acc.requiresQPM = true;
      }
      return acc;
    },
    { requiresSCB: false, requiresALoRa: false, requiresQPM: false },
  );

  const ai: ReqAdditionalInformationDTO = { ...additionalInformation };

  // Backend defaulting rules
  if (flags.requiresSCB && ai.scbAmountOfMeasures < 12)
    ai.scbAmountOfMeasures = 12;
  if (!flags.requiresSCB && ai.scbAmountOfMeasures > 0)
    ai.scbAmountOfMeasures = 0;

  if (flags.requiresALoRa && ai.aloraAmountOfMeasures === 0)
    ai.aloraAmountOfMeasures = 1;
  if (!flags.requiresALoRa && ai.aloraAmountOfMeasures > 0)
    ai.aloraAmountOfMeasures = 0;

  if (flags.requiresQPM && ai.qpmCycleAmountPerYear === 0)
    ai.qpmCycleAmountPerYear = 1;
  if (!flags.requiresQPM && ai.qpmCycleAmountPerYear > 0)
    ai.qpmCycleAmountPerYear = 0;

  return ai;
}

function reconcileDerivedPests(
  current: Record<ID, PestSelectionDTO> | null | undefined,
  derived: Record<ID, PestSnapDTO>,
): Record<ID, PestSelectionDTO> {
  const out: Record<ID, PestSelectionDTO> = { ...(current ?? {}) };

  // Apply derived set.
  Object.keys(derived).forEach((id) => {
    const snap = derived[id];
    const existing = out[id];

    if (!existing) {
      out[id] = {
        pest: snap,
        selected: true,
        locked: true,
        source: "DERIVED" satisfies PestSource,
      };
      return;
    }

    out[id] = {
      ...existing,
      pest: snap,
      selected: true,
      locked: true,
      source: "DERIVED",
    };
  });

  // No longer derived: if it was locked+derived => unlock and set MANUAL.
  Object.keys(out).forEach((id) => {
    const ps = out[id];
    const stillDerived = derived[id] !== undefined;
    if (!stillDerived && ps.locked && ps.source === "DERIVED") {
      out[id] = {
        ...ps,
        locked: false,
        source: "MANUAL" satisfies PestSource,
      };
    }
  });

  return out;
}

function recomputeRequirementDerivedFields(
  req: RequirementDTO,
): RequirementDTO {
  const sels = req.selectedMonitors ?? [];
  const snaps = req.monitorSnaps ?? {};
  const prevSummary = req.monitorSummary;

  // Pre-index previous totalPrice by monitorId to preserve client behavior.
  const prevTotalPriceByMonitorId: Record<ID, number> = {};
  (prevSummary?.monitors ?? []).forEach((m) => {
    prevTotalPriceByMonitorId[m.monitorId] = m.totalPrice ?? 0;
  });

  const byMonitorId: Record<ID, RequirementMonitorSummaryItemDTO> = {};

  let totalInstall = 0;
  let totalService = 0;

  // Use a composite key map to aggregate typeSummary rows.
  // Key format: `${monitorType}|||${iotClass ?? ""}`
  const typeSummaryByKey: Record<string, TypeSummaryItemDTO> = {};

  const derivedPests: Record<ID, PestSnapDTO> = {};

  // Track IoT roles to create derived dependents.
  let requiredSimAmount = 0;
  let requiredPortalAmount = 0;

  const incType = (
    monitorType: MonitorType,
    amount: number,
    iotClass?: MonitorIoTClass,
  ) => {
    const key = `${monitorType}|||${iotClass ?? ""}`;
    const existing = typeSummaryByKey[key];
    if (existing) {
      existing.amount += amount;
      return;
    }
    typeSummaryByKey[key] = {
      monitorType,
      iotClass,
      amount,
    };
  };

  // Single pass over selected monitors.
  sels.forEach((sel) => {
    const snap = snaps[sel.monitorId];
    const amount = sel.amount ?? 0;

    // Even if we don't have a snapshot, still increment typeSummary for offline visibility.
    // (No timing/price/pests without snapshot.)
    if (!snap) {
      incType(sel.monitorTypeKey, amount);
      return;
    }

    const iotClass = sel.ioTSpec?.class ?? snap.capabilities?.iot?.class;

    // --- Summary list (grouped by monitorId)
    const existing = byMonitorId[sel.monitorId];
    if (!existing) {
      byMonitorId[sel.monitorId] = {
        monitorId: sel.monitorId,
        monitorTypeKey: sel.monitorTypeKey,
        iotClass,
        hasLethalAgent: sel.hasLethalAgent,
        totalAmount: amount,
        totalInstallationTime: 0,
        totalServiceTime: 0,
        basePrice: snap.basePrice?.price ?? 0,
        totalPrice: prevTotalPriceByMonitorId[sel.monitorId] ?? 0,
      };
    } else {
      existing.totalAmount += amount;
      existing.hasLethalAgent = existing.hasLethalAgent || sel.hasLethalAgent;
      existing.monitorTypeKey = sel.monitorTypeKey;
      if (!existing.iotClass) {
        existing.iotClass = iotClass;
      }
    }

    // --- Totals (times)
    totalInstall += (snap.times?.install ?? 0) * amount;
    totalService += (snap.times?.maintain ?? 0) * amount;

    // --- Type summaries
    incType(sel.monitorTypeKey, amount);
    if (
      (sel.monitorTypeKey === MonitorType.Checkpoint ||
        sel.monitorTypeKey === MonitorType.FlyExterminator) &&
      shouldIncludeInCheckpointService(sel, snap)
    ) {
      incType(MonitorType.Checkpoint, amount);
    }
    if (sel.monitorTypeKey === MonitorType.IoT && iotClass) {
      // for IoT, we also create class-specific rows
      incType(MonitorType.IoT, amount, iotClass);
      if (shouldIncludeInCheckpointService(sel, snap)) {
        incType(MonitorType.Checkpoint, amount);
      }

      const next = accumulateDerivedIoTDependents(
        {
          requiredSimAmount,
          requiredPortalAmount,
        },
        iotClass,
        amount,
      );
      requiredSimAmount = next.requiredSimAmount;
      requiredPortalAmount = next.requiredPortalAmount;
    }

    // --- Derived pests
    (snap.pests ?? []).forEach((p) => {
      derivedPests[p.sourceId] = p;
    });
  });

  // IoT derived dependents:
  if (requiredSimAmount > 0) {
    // incType(MonitorType.IoT, requiredSimAmount);
    incType(MonitorType.IoT, requiredSimAmount, MonitorIoTClass.IoTSim);
  }
  if (requiredPortalAmount > 0) {
    // incType(MonitorType.IoT, requiredPortalAmount);
    incType(MonitorType.IoT, requiredPortalAmount, MonitorIoTClass.IoTPortal);
  }

  const monitors = Object.keys(byMonitorId).map((k) => byMonitorId[k]);

  // Optional total price: backend uses pointer only if non-zero.
  const sumTotalPrice = monitors.reduce(
    (acc, m) => acc + (m.totalPrice ?? 0),
    0,
  );
  const totalPrice = sumTotalPrice !== 0 ? sumTotalPrice : undefined;

  const typeSummary = Object.keys(typeSummaryByKey).map(
    (k) => typeSummaryByKey[k],
  );

  const monitorSummary: RequirementMonitorSummaryDTO = {
    monitors,
    typeSummary,
    totalInstallationTime: totalInstall,
    totalServiceTime: totalService,
    totalPrice,
  };

  let additionalInformation = applyAdditionalInformationDefaults(
    req.additionalInformation,
    req.selectedMonitors,
    req.monitorSnaps,
  );
  additionalInformation = {
    ...additionalInformation,
    installationTime: monitorSummary.totalInstallationTime,
    maintenanceTime: monitorSummary.totalServiceTime,
  };

  const selectedPests = reconcileDerivedPests(req.selectedPests, derivedPests);

  return {
    ...req,
    monitorSummary,
    additionalInformation,
    selectedPests,
  };
}
